/**
 * Salary currency and pay frequency, in one place.
 *
 * Both lists existed twice in this app, declared verbatim in `JobCreatePopup` and again in
 * `JobWorkspace`, and pay frequency additionally existed as a THIRD, unconstrained copy: the
 * workspace rendered it as a free-text box while the create popup offered a dropdown. So a job
 * created through the popup got `MONTHLY` and the same job edited in the workspace could get
 * `monthly`, `Per month`, or `''` -- none of which any display map recognises, so all of them
 * fall through to the "annual" default on the public site.
 *
 * `salaryFrequency` is a plain `String` on the backend (`Job.java`), with no enum and no
 * validation, so nothing downstream was going to catch that. The constraint has to live here.
 *
 * **Ordering is descending by period**, matching the public UI's list, so the two repos read the
 * same way round. The labels are the Control Center's friendlier ones ("Per Month" rather than
 * "MONTHLY"), because a label is display only.
 *
 * `BI_WEEKLY` was requested and is **not** shipping. schema.org's `unitText` for `baseSalary`
 * accepts only HOUR | DAY | WEEK | MONTH | YEAR, and the public site maps into exactly that set,
 * so a bi-weekly job would either emit an invalid `unitText` (Google Jobs drops the salary) or be
 * mapped to WEEK at twice the real amount. Representing it honestly means halving the figure,
 * which is a data transform with its own correctness question, not a label. That was put to the
 * requester with the cost attached and declined. Do not add it back without the transform.
 */

export interface SalaryOption {
  value: string;
  label: string;
}

export const SALARY_CURRENCIES: SalaryOption[] = [
  { value: 'INR', label: '₹ INR: Indian Rupee' },
  { value: 'USD', label: '$ USD: US Dollar' },
  { value: 'AED', label: 'AED: UAE Dirham' },
  { value: 'EUR', label: '€ EUR: Euro' },
  { value: 'GBP', label: '£ GBP: British Pound' },
  { value: 'SGD', label: 'SGD: Singapore Dollar' },
  { value: 'SAR', label: 'SAR: Saudi Riyal' },
];

export const SALARY_FREQUENCIES: SalaryOption[] = [
  { value: 'ANNUALLY', label: 'Per Year' },
  { value: 'MONTHLY', label: 'Per Month' },
  { value: 'WEEKLY', label: 'Per Week' },
  { value: 'DAILY', label: 'Per Day' },
  { value: 'HOURLY', label: 'Per Hour' },
];

/**
 * The same list with a leading blank, for an edit control that has to be able to CLEAR the field.
 * Without it the dropdown is write-once: once a frequency is set there is no way back to unset.
 */
export const SALARY_FREQUENCY_OPTIONS: SalaryOption[] = [
  { value: '', label: 'Not specified' },
  ...SALARY_FREQUENCIES,
];

const FREQUENCY_VALUES = new Set(SALARY_FREQUENCIES.map((o) => o.value));

/**
 * Canonical value, or `undefined` for anything unrecognised.
 *
 * Used on the write path so a legacy free-text value cannot be echoed back into the database.
 * The alias table covers what people actually typed into the old text box; anything outside it
 * returns `undefined` and is left alone rather than guessed at.
 */
const FREQUENCY_ALIASES: Record<string, string> = {
  YEARLY: 'ANNUALLY',
  ANNUAL: 'ANNUALLY',
  PER_YEAR: 'ANNUALLY',
  PER_ANNUM: 'ANNUALLY',
  PA: 'ANNUALLY',
  YEAR: 'ANNUALLY',
  PER_MONTH: 'MONTHLY',
  MONTH: 'MONTHLY',
  PM: 'MONTHLY',
  PER_WEEK: 'WEEKLY',
  WEEK: 'WEEKLY',
  PER_DAY: 'DAILY',
  DAY: 'DAILY',
  PER_HOUR: 'HOURLY',
  HOUR: 'HOURLY',
  HR: 'HOURLY',
};

export const normalizeSalaryFrequency = (v: unknown): string | undefined => {
  const n = String(v ?? '')
    .trim()
    .replace(/[\s-]+/g, '_')
    .toUpperCase();
  if (!n) return undefined;
  if (FREQUENCY_VALUES.has(n)) return n;
  return FREQUENCY_ALIASES[n];
};

/** Display text for a stored value. Falls back to the raw value so dirty rows stay legible. */
export const salaryFrequencyLabel = (v: unknown): string => {
  const canonical = normalizeSalaryFrequency(v);
  if (!canonical) return String(v ?? '');
  return SALARY_FREQUENCIES.find((o) => o.value === canonical)?.label ?? String(v ?? '');
};
